import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { renameKeysFromSDKRequestToRESTRequest } from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient } from '@wix/sdk-types';
import * as ambassadorWixAutomationsV1AutomationsCustomTrigger from './automations-v1-automations-custom-trigger-custom-trigger.http.js';

export interface MainEntity {
  /** @format GUID */
  _id?: string;
  tags?: Tags;
}

export interface Tags {
  privateTags?: TagList;
  tags?: TagList;
}

export interface TagList {
  /**
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface RunTriggerRequest {
  /**
   * Trigger ID. You can find this in the custom trigger configuration in the automations builder.
   * @maxLength 100
   */
  triggerId?: string;
  /**
   * Optional payload object to pass to run trigger. Define the payload schema from sample data in the custom trigger configuration in the
   * automations builder.
   */
  payload?: Record<string, any> | null;
}

export interface RunTriggerResponse {}

export interface BulkUpdateMainEntityTagsRequest {
  /**
   * @maxSize 100
   * @maxLength 50
   */
  entityIds?: string[];
  assignTags?: Tags;
  unassignTags?: Tags;
}

export interface BulkUpdateMainEntityTagsResponse {}

export interface BulkUpdateMainEntityTagsByFilterRequest {
  filter?: Record<string, any> | null;
  assignTags?: Tags;
  unassignTags?: Tags;
}

export interface BulkUpdateMainEntityTagsByFilterResponse {}

/**
 * Runs the automation associated with the trigger ID that you pass to it.
 * @public
 * @permissionId AUTOMATIONS.TRIGGER_WEBHOOK
 * @applicableIdentity APP
 * @fqn com.wixpress.crm.automations.automations_custom_trigger.AutomationsCustomTriggerService.RunTrigger
 */
export async function runTrigger(options?: RunTriggerOptions): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    triggerId: options?.triggerId,
    payload: options?.payload,
  });

  const reqOpts =
    ambassadorWixAutomationsV1AutomationsCustomTrigger.runTrigger(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          triggerId: '$[0].triggerId',
          payload: '$[0].payload',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RunTriggerOptions {
  /**
   * Trigger ID. You can find this in the custom trigger configuration in the automations builder.
   * @maxLength 100
   */
  triggerId?: string;
  /**
   * Optional payload object to pass to run trigger. Define the payload schema from sample data in the custom trigger configuration in the
   * automations builder.
   */
  payload?: Record<string, any> | null;
}

/** @internal
 * @documentationMaturity preview
 * @permissionId AUTOMATIONS.TRIGGER_WEBHOOK
 * @applicableIdentity APP
 * @fqn com.wixpress.crm.automations.automations_custom_trigger.AutomationsCustomTriggerService.BulkUpdateMainEntityTags
 */
export async function bulkUpdateMainEntityTags(
  options?: BulkUpdateMainEntityTagsOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    entityIds: options?.entityIds,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixAutomationsV1AutomationsCustomTrigger.bulkUpdateMainEntityTags(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          entityIds: '$[0].entityIds',
          assignTags: '$[0].assignTags',
          unassignTags: '$[0].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateMainEntityTagsOptions {
  /**
   * @maxSize 100
   * @maxLength 50
   */
  entityIds?: string[];
  assignTags?: Tags;
  unassignTags?: Tags;
}

/** @internal
 * @documentationMaturity preview
 * @permissionId AUTOMATIONS.TRIGGER_WEBHOOK
 * @applicableIdentity APP
 * @fqn com.wixpress.crm.automations.automations_custom_trigger.AutomationsCustomTriggerService.BulkUpdateMainEntityTagsByFilter
 */
export async function bulkUpdateMainEntityTagsByFilter(
  options?: BulkUpdateMainEntityTagsByFilterOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixAutomationsV1AutomationsCustomTrigger.bulkUpdateMainEntityTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0].filter',
          assignTags: '$[0].assignTags',
          unassignTags: '$[0].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateMainEntityTagsByFilterOptions {
  filter?: Record<string, any> | null;
  assignTags?: Tags;
  unassignTags?: Tags;
}
